"use client";

import { Box, Snowflake, Squircle, Wind, Sun, Wrench } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const features = [{
  icon: Box,
  title: "Rapid Response Deployment",
  description: "Our agile support teams are ready to mobilize within hours, ensuring your critical operations remain uninterrupted during unexpected technical challenges."
}, {
  icon: Snowflake,
  title: "Emergency System Stabilization",
  description: "We provide immediate diagnostic and remediation services to stabilize failing infrastructure, preventing downtime and protecting your core business assets."
}, {
  icon: Squircle,
  title: "On-Demand Technical Audits",
  description: "Gain clarity with targeted, high-impact audits that identify vulnerabilities in your current setup, providing actionable insights for immediate improvement."
}, {
  icon: Wind,
  title: "Seamless Integration Support",
  description: "We bridge the gap between legacy systems and modern workflows, ensuring your ad-hoc technical needs align perfectly with your long-term strategy."
}, {
  icon: Sun,
  title: "Scalable Resource Allocation",
  description: "Access specialized expertise exactly when you need it, allowing your team to scale up for complex projects without the overhead of permanent hires."
}, {
  icon: Wrench,
  title: "Precision Troubleshooting",
  description: "Our experts utilize advanced diagnostic tools to pinpoint root causes of persistent issues, delivering permanent fixes rather than temporary patches."
}];
function FeatureTile({
  feature,
  index
}: {
  feature: (typeof features)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = feature.icon;
  return <div ref={ref} data-index={index} className="group relative rounded-lg bg-card border border-border p-7 transition-all duration-300 ease-out hover:border-primary hover:-translate-y-1 hover:shadow-lg" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    transition: "opacity 620ms cubic-bezier(0.22, 1, 0.36, 1), transform 620ms cubic-bezier(0.22, 1, 0.36, 1), border-color 300ms ease-out",
    transitionDelay: `${index * 80}ms`
  }}>
      <div className="mb-6">
        <Icon strokeWidth={1.5} className="size-6 text-primary transition-colors duration-300 group-hover:text-primary" style={{
        strokeDasharray: 100,
        strokeDashoffset: isVisible ? 0 : 100,
        transition: "stroke-dashoffset 600ms ease-out, color 300ms ease-out",
        transitionDelay: `${index * 80 + 200}ms`
      }} />
      </div>
      <h3 className="font-default text-xl md:text-2xl font-semibold tracking-tight text-foreground mb-3 leading-snug">
        {feature.title}
      </h3>
      <p className="text-sm md:text-[0.9375rem] leading-relaxed text-muted-foreground">
        {feature.description}
      </p>
    </div>;
}
export default function AdhocSupport() {
  return <section data-section-id="16376" id="when-ad-hoc-support-can-help" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-8">
      <div className="mx-auto max-w-[1360px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <h2 className="font-default text-4xl md:text-5xl lg:text-[3.25rem] font-semibold leading-[1.02] tracking-tight text-foreground">
                {"Expert support, "}
                <em className="italic font-medium text-primary not-italic-fallback">
                  <span className="italic">on your terms</span>
                </em>
                {"."}
              </h2>
              <p className="mt-8 text-lg md:text-xl leading-relaxed text-muted-foreground max-w-[50ch]">
                {"When your internal team faces unexpected hurdles, our ad-hoc support services provide the specialized expertise required to maintain momentum and ensure operational excellence."}
              </p>
              <div className="mt-10">
                <Link to="/GetInTouchPage" className="inline-flex items-center gap-2 font-default font-medium text-foreground border-b border-foreground/80 pb-1 transition-colors duration-300 hover:text-primary hover:border-primary">
                  <span>Request immediate assistance</span>
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              {features.map((feature, i) => <FeatureTile key={feature.title} feature={feature} index={i} />)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}